import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { debounce } from 'core/libs/lodash';

import themePropTypes from 'core/utils/prop-types/theme';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';
import { Desktop } from 'core/components/breakpoint';

import Swipe, { Slide } from 'core/components/Swipe';
import Bullets from 'core/components/Bullets';
import Counter from 'core/components/Counter';
import Link from 'core/components/Link';

import { resolveScopedStyles } from 'core/utils/styled-jsx';

import styles from './index.styl';
import HeaderArrows from './HeaderArrows';


/**
 * @author Alexey Rezakov <a.rezakov@rambler-co.ru>
 */
class BaseDrum extends PureComponent {
  static propTypes = {
    /**
     * Катрочки для слайдера.
     */
    card: PropTypes.func.isRequired,
    /**
     * Данные о топиках.
     */
    content: PropTypes.array,

    /** @ignore */
    isMobile: PropTypes.bool,
    /**
     * Расстояние между карточками в драме.
     * Также, отступ для стрелок навигации в шапке Drum
     */
    spaceBetween: PropTypes.number,
    /**
     * Ширина карточки. Может быть задана числом или в процентах.
     *
     * Игнорируется на мобильном лейауте.
     */
    itemWidth: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    /**
     * Ширина карточки на мобильном лейауте
     */
    itemWidthMobile: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    /**
     * Ссылка на все материалы. Отображается последним слайдом.
     * Также может располагаться под слайдером или рядом с заголовком если задан linkPosition.
     */
    link: PropTypes.string,
    /**
     * Текст ссылки
     */
    linkTitle: PropTypes.node,

    /**
     * Задаёт смещение стрелок относительно исходного положения
     * (положительные значения - наружу, отрицательные - внутрь)
     */
    arrowsShift: PropTypes.number,

    /**
     * Задаёт смещение стрелок по вертикали.
     * Отсчет начинается сверху.
     * По умолчанию стрелки позиционируются посередине.
     */
    arrowsShiftVertical: PropTypes.number,

    /**
     * Можно задать высоту иконки навигационной стрелки.
     * По умолчанию, значение берется из темы.
     */
    arrowHeight: PropTypes.number,

    /**
     * Можно задать ширину иконки навигационной стрелки.
     * По умолчанию, значение берется из темы.
     */
    arrowWidth: PropTypes.number,
    /**
     * Отключает bullets и счётчик слайдов на мобилках
     */
    noCounter: PropTypes.bool,
    /** @ignore */
    theme: themePropTypes(`
      {
        animations: {
          hover,
        },
        controls: {
          drum: {
            const: {
              bottomSpacing,
            },
            link: {
              font
            },
            arrow: {
              Left,
              Right,
              fade: {
                outer,
                inner,
              }
              idle: {
                color
              },
              hover: {
                color
              },
            },
          },
        },
      }
    `),

    /**
     * При изменениии активной вкладки будет осуществлен переход к первому слайду
     */
    activeTab: PropTypes.number,

    /**
     * Включаем дополнительные контролы навигации, над слайдером.
     */
    showHeaderArrows: PropTypes.bool,

    /**
     * Показываем основные стрелки навигации по ховеру
     */
    showArrowsOnHover: PropTypes.bool,

    /**
     * Скрываем основные контролы навигации
     */
    hideArrows: PropTypes.bool,
    /**
     * Можно управлять количеством Bullets
     */
    counterSwitchThreshold: PropTypes.number,

    /**
     * Цвет тени 1, под стрелки вперед / назад
     * Значение цвета подставляется в формулу:
     * `linear-gradient(to left|right, ${fadeOuter} 0%, ${fadeInner} 100%)`
     *
     * Переопределяет значение из темы
     */
    fadeOuter: PropTypes.string,
    /**
      * Цвет тени 2, под стрелки вперед / назад
      * Значение цвета подставляется в формулу:
      * `linear-gradient(to left|right, ${fadeOuter} 0%, ${fadeInner} 100%)`
      *
      * Переопределяет значение из темы
      */
    fadeInner: PropTypes.string,

    /**
     * Цвет тени контрола "Вперед".
     *
     * Переопределяет значения fadeOuter, fadeInner и значения в из темы
     */
    fadePrev: PropTypes.string,

    /**
     * Цвет тени контрола "Назад".
     *
     * Переопределяет значения fadeOuter, fadeInner и значения в из темы
     */
    fadeNext: PropTypes.string,

    /** @ignore */
    dataQa: PropTypes.string,

    /**
     * Props для карточки.
     * Чтобы не использовать bindPropsHOC в рендере компонента.
     *
     * Для понимания:
     *  использование bindPropsHOC в рендере компонента, приводит к ненужным перерендерам.
     */
    cardProps: PropTypes.object,

    /**
      * Взможность показать буллеты на десктопе
    */
    showBulletsOnDesktop: PropTypes.bool,

    /**
     * Включает циклическую прокрутку слайдов
     */
    loop: PropTypes.bool,

    /**
     * Справочник замен карточек на другие компоненты.
     *
     * В качестве ключа должен выступать индекс карточки которую нужно заменить,
     * а в качестве значения компонент на который будет заменена карточка.
     *
     * Элемент отрисованной карточки с данными будет передан в новый компонент
     * в качестве пропса `card`.
     */
    cardsReplacement: PropTypes.object,

    /**
     * Можно переопределить левую иконку стрелки.
     * По умолчанию берется из темы.
     */
    leftIcon: PropTypes.func,

    /**
     * Можно переопределить правую иконку стрелки.
     * По умолчанию берется из темы.
     */
    rightIcon: PropTypes.func,

    /**
     * Дополнительный класс для контейнера
     */
    containerClass: PropTypes.string,

    /**
     * Дополнительный класс для wrapper
     */
    wrapperClass: PropTypes.string,

    /**
     * autoHeight для swiper
     */
    autoHeight: PropTypes.bool,
  };

  static defaultProps = {
    itemWidth: `${100 / 3}%`,
    itemWidthMobile: '80%',
    spaceBetween: 20,
    linkTitle: 'Все материалы',
    arrowsShift: 0,
    counterSwitchThreshold: 7,
    loop: false,
  };

  state = {
    active: 0,
    swiperWidth: null,
  };

  componentDidMount() {
    this.updateSwiperWidth();
    window.addEventListener('resize', this.updateSwiperWidth);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.activeTab !== nextProps.activeTab) {
      this.goto(0);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateSwiperWidth);
    this.updateSwiperWidth.cancel();
  }

  updateSwiperWidth = debounce(() => {
    const swiperWidth = this.wrapper && this.wrapper.clientWidth;
    this.setState({ swiperWidth });
  }, 500);

  get nextActive() {
    return parseInt(this.state.active + this.visibleSlides, 10);
  }

  get prevActive() {
    return parseInt(this.state.active - this.visibleSlides, 10);
  }

  get visibleSlides() {
    const { swiperWidth } = this.state;
    const { spaceBetween } = this.props;
    let visibleSlides = 100 / parseInt(this.slideWidth, 10);

    if (!/%$/.test(this.slideWidth) && swiperWidth) {
      /*
      * При числовом формате не учитывалось смещение на spaceBetween.
      * 1 прибавляем для компенсации погрешности при расчёте visibleSlides на пограничном значении
      */
      visibleSlides = (swiperWidth + spaceBetween + 1) / parseInt(this.slideWidth, 10);
    }

    return visibleSlides;
  }

  get slideWidth() {
    const {
      isMobile,
      itemWidth,
      itemWidthMobile,
    } = this.props;

    return isMobile ? itemWidthMobile : itemWidth;
  }

  prev = () => this.props.loop ? this.swipe.prev() : this.goto(this.prevActive);
  next = () => this.props.loop ? this.swipe.next() : this.goto(this.nextActive);
  goto = (index) => this.swipe.goto(index);

  render() {
    const {
      content,
      card: Card,
      linkTitle,
      spaceBetween,
      autoHeight,
      link,
      arrowsShift,
      arrowsShiftVertical,
      arrowHeight,
      arrowWidth,
      noCounter,
      showHeaderArrows,
      showArrowsOnHover,
      counterSwitchThreshold,
      hideArrows,
      fadeOuter,
      fadeInner,
      fadePrev,
      fadeNext,
      dataQa,
      cardProps,
      isMobile,
      showBulletsOnDesktop,
      loop,
      cardsReplacement,
      containerClass,
      wrapperClass,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      theme: {
        animations: {
          hover: animationHover,
        },
        controls: {
          drum: drumAtoms,
          drum: {
            arrow: {
              Left: LeftIconFromTheme,
              Right: RightIconFromTheme,
              fade: {
                outer: fadeOuterTheme,
                inner: fadeInnerTheme,
              },
            },
          },
        },
      },
    } = this.props;

    // legacy
    const Left = LeftIcon || LeftIconFromTheme;
    const Right = RightIcon || RightIconFromTheme;
    const arrowFadeOuter = fadeOuter || fadeOuterTheme;
    const arrowFadeInner = fadeInner || fadeInnerTheme;
    // modern
    const arrowFadePrev = fadePrev
      || `linear-gradient(to left, ${arrowFadeOuter} 0%, ${arrowFadeInner} 100%)`;
    const arrowFadeNext = fadeNext
      || `linear-gradient(to right, ${arrowFadeOuter} 0%, ${arrowFadeInner} 100%)`;
    const replacements = cardsReplacement || {};

    const { active } = this.state;

    const scope = resolveScopedStyles(
      <scope>
        <style jsx>{`
          .${styles.arrow}
            & :global([id*="$"])
              fill ${drumAtoms.arrow.idle.color}
              transition fill ${animationHover}
            & :global([id*="!"])
              transition stroke ${animationHover}
          .${styles.card}
            margin-right ${spaceBetween + 'px'}
          .link
            font ${drumAtoms.link.font}
        `}</style>
      </scope>
    );

    const slides = content.map((topic, position) => {
      const Replacement = replacements[position];
      const allCardProps = {
        content: topic,
        position: position,
        ...cardProps,
      };

      const card = <Card {...allCardProps} />;
      const item = Replacement ? <Replacement {...allCardProps} card={card} /> : card;

      return (
        <Slide key={topic.id} width={this.slideWidth}>
          <div className={scope.wrapClassNames(styles.card)}>
            {item}
          </div>
        </Slide>
      );
    });

    link && slides.push(
      <Slide key={slides.length} width={this.slideWidth}>
        <Link
          to={link}
          type='tertiary'
          className={scope.wrapClassNames('link', styles.card, styles.end)}
        >
          <span className={styles.endText}>{linkTitle}</span>
        </Link>
      </Slide>
    );

    const isStart = active <= 0;
    const isEnd = active >= slides.length - this.visibleSlides;

    return (
      <div className={styles.baseDrum} {...dataQa && { 'data-qa': dataQa + '-base-drum' }}>
        <style jsx>{`
          .${styles.button}
            transition opacity ${animationHover}
            &:hover .${styles.arrow} :global([id*="$"])
              fill ${drumAtoms.arrow.hover.color}
            &:hover .${styles.arrow} :global([id*="!"])
              stroke ${drumAtoms.arrow.hover.stroke}
          .${styles.arrow}
            top ${arrowsShiftVertical ? arrowsShiftVertical + 'px' : '50%'}
          .wrapperSpacer
            margin-right ${-spaceBetween + 'px'}
          .${styles.buttonNext}
            background ${arrowFadePrev}
            :global(svg)
              right ${arrowsShift * -1}px
          .${styles.buttonPrev}
            background ${arrowFadeNext}
            :global(svg)
              left ${arrowsShift * -1}px
          .${styles.bullets}
            margin ${drumAtoms.const.bulletsMargin}
          .${styles.headerArrows}
            margin-bottom ${spaceBetween}px
        `}</style>

        {showHeaderArrows && (
          <Desktop>
            <div className={styles.headerArrows}>
              <HeaderArrows
                nextHandler={this.next}
                prevHandler={this.prev}
                isStart={isStart}
                isEnd={isEnd}
              />
            </div>
          </Desktop>
        )}

        <div className={cx(styles.outerWrapper, showArrowsOnHover && styles._showArrowsOnHover)}>
          {!hideArrows && (
            <Desktop>
              {(loop || !isStart) && (
                <div
                  className={cx(
                    'drumButton', // используется как глобальный селектор CSS
                    styles.button,
                    styles.buttonPrev,
                  )}
                  onClick={this.prev}
                >
                  <Left
                    className={scope.wrapClassNames(styles.arrow)}
                    height={arrowHeight ? arrowHeight : drumAtoms.arrow.height}
                    width={arrowWidth ? arrowWidth : drumAtoms.arrow.width}
                  />
                </div>
              )}
              {(loop || !isEnd) && (
                <div
                  className={cx(
                    'drumButton', // используется как глобальный селектор CSS
                    styles.button,
                    styles.buttonNext,
                  )}
                  onClick={this.next}
                >
                  <Right
                    className={scope.wrapClassNames(styles.arrow)}
                    height={arrowHeight ? arrowHeight : drumAtoms.arrow.height}
                    width={arrowWidth ? arrowWidth : drumAtoms.arrow.width}
                  />
                </div>
              )}
            </Desktop>
          )}

          <div
            className={styles.wrapper}
            ref={instance => (this.wrapper = instance)}
          >
            <div className='wrapperSpacer'>
              <Swipe
                containerClass={containerClass}
                wrapperClass={wrapperClass}
                ref={instance => (this.swipe = instance)}
                onSnapIndexChange={({ index }) => this.setState({ active: index })}
                loop={loop}
                autoHeight={autoHeight}
                freeMode={{
                  enabled: true,
                  sticky: false,
                  momentumBounce: false,
                  momentumVelocityRatio: 0.5,
                }}
              >
                {slides}
              </Swipe>
            </div>
          </div>
          {(isMobile || showBulletsOnDesktop) && !noCounter && (
            <div className={cx(styles.bullets)}>
              {slides.length >= counterSwitchThreshold
                ? <Counter total={slides.length} currentIndex={active} />
                : (
                  <Bullets
                    currentIndex={active}
                    total={slides.length}
                    onChange={this.goto}
                  />
                )
              }
            </div>
          )}
        </div>
        <scope.styles />
      </div>
    );
  }
}

const BaseDrumComponent = withBreakpoint(withTheme(BaseDrum));
BaseDrumComponent.displayName = 'BaseDrum';

export default BaseDrumComponent;
export { BaseDrum as StorybookComponent };
